import { supabase } from './supabase';
import { CharacterVoice, SavedVoiceSample } from '../types';
import { uploadToR2 } from './storage';
import { generateId } from './utils';

// ============================================
// R2 STORAGE HELPERS FOR VOICE
// ============================================

/**
 * Upload audio blob to R2 in the voice folder
 * Path: ezart-assets/character/final/{projectId}/{characterId}/voice/{filename}
 */
export async function uploadVoiceAudioToR2(
  blob: Blob,
  userId: string,
  projectId: string,
  characterId: string,
  filename: string,
  isTemp: boolean = false
): Promise<string> {
  const file = new File([blob], filename, { type: blob.type || 'audio/wav' });
  const storageType = isTemp ? 'temp' : 'final';
  const path = `character/${storageType}/${projectId}/${characterId}/voice`;
  const url = await uploadToR2(file, path, userId);
  return url;
}

/**
 * Upload reference audio for voice cloning (stored in temp until character finalized)
 * Validates audio duration (5-15 seconds recommended)
 */
export async function uploadReferenceAudio(
  audioBlob: Blob,
  userId: string,
  projectId: string,
  characterId: string
): Promise<string> {
  const filename = `ref_${Date.now()}.wav`;
  return uploadVoiceAudioToR2(audioBlob, userId, projectId, characterId, filename, true);
}

/**
 * Upload generated preview audio (stored in final storage)
 */
export async function uploadVoicePreview(
  audioBlob: Blob,
  userId: string,
  projectId: string,
  characterId: string
): Promise<string> {
  const filename = `preview_${Date.now()}.wav`;
  return uploadVoiceAudioToR2(audioBlob, userId, projectId, characterId, filename, false);
}

/**
 * Upload voice embedding (from ComfyUI SaveVoiceNode)
 */
export async function uploadVoiceEmbedding(
  embeddingBlob: Blob,
  userId: string,
  projectId: string,
  characterId: string
): Promise<string> {
  const filename = `embedding_${Date.now()}.bin`;
  return uploadVoiceAudioToR2(embeddingBlob, userId, projectId, characterId, filename, false);
}

// ============================================
// CHARACTER VOICE CRUD OPERATIONS
// ============================================

/**
 * Save a character voice configuration
 */
export async function saveCharacterVoice(voice: CharacterVoice): Promise<CharacterVoice> {
  try {
    // For updates, preserve existing saved_samples; for new records, initialize to empty array
    let savedSamples: SavedVoiceSample[] = [];
    if (voice.id) {
      const existing = await loadCharacterVoice(voice.id);
      if (existing && (existing as any).saved_samples) {
        savedSamples = (existing as any).saved_samples;
      }
    }

    const { data, error } = await supabase
      .from('character_voices')
      .upsert(
        {
          id: voice.id,
          character_id: voice.character_id,
          user_id: voice.user_id,
          voice_mode: voice.voice_mode,
          preset_speaker: voice.preset_speaker || null,
          reference_audio_url: voice.reference_audio_url || null,
          reference_transcript: voice.reference_transcript || null,
          voice_description: voice.voice_description || null,
          language: voice.language,
          style_instruction: voice.style_instruction || null,
          voice_embedding_url: voice.voice_embedding_url || null,
          preview_audio_url: voice.preview_audio_url || null,
          preview_text: voice.preview_text || null,
          saved_samples: savedSamples,
          created_at: voice.created_at,
          updated_at: Date.now(),
        },
        { onConflict: 'id' }
      );

    if (error) throw error;
    return voice;
  } catch (error) {
    console.error('Failed to save character voice:', error);
    throw error;
  }
}

/**
 * Load a character voice by ID
 */
export async function loadCharacterVoice(voiceId: string): Promise<CharacterVoice | null> {
  try {
    const { data, error } = await supabase
      .from('character_voices')
      .select('*')
      .eq('id', voiceId)
      .maybeSingle();

    if (error) throw error;
    return data as CharacterVoice | null;
  } catch (error) {
    console.error('Failed to load character voice:', error);
    throw error;
  }
}

/**
 * Get voice configuration for a character
 * Returns null if no voice is configured
 */
export async function getVoiceByCharacterId(characterId: string): Promise<CharacterVoice | null> {
  try {
    const { data, error } = await supabase
      .from('character_voices')
      .select('*')
      .eq('character_id', characterId)
      .maybeSingle();

    if (error) throw error;
    return data as CharacterVoice | null;
  } catch (error) {
    console.error('Failed to load character voice:', error);
    return null;
  }
}

/**
 * Get voice configurations for multiple characters in a single query (batch)
 * Returns a map of character_id -> CharacterVoice
 */
export async function getVoicesByCharacterIds(characterIds: string[]): Promise<Map<string, CharacterVoice>> {
  if (characterIds.length === 0) return new Map();

  try {
    const { data, error } = await supabase
      .from('character_voices')
      .select('*')
      .in('character_id', characterIds);

    if (error) throw error;

    const voiceMap = new Map<string, CharacterVoice>();
    (data || []).forEach((voice: CharacterVoice) => {
      voiceMap.set(voice.character_id, voice);
    });
    return voiceMap;
  } catch (error) {
    console.error('Failed to batch load character voices:', error);
    return new Map();
  }
}

/**
 * Get all voices for a user
 */
export async function getUserCharacterVoices(userId: string): Promise<CharacterVoice[]> {
  try {
    const { data, error } = await supabase
      .from('character_voices')
      .select('*')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false });

    if (error) throw error;
    return (data || []) as CharacterVoice[];
  } catch (error) {
    console.error('Failed to load user character voices:', error);
    throw error;
  }
}

/**
 * Delete a character voice configuration
 */
export async function deleteCharacterVoice(voiceId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('character_voices')
      .delete()
      .eq('id', voiceId);

    if (error) throw error;
  } catch (error) {
    console.error('Failed to delete character voice:', error);
    throw error;
  }
}

/**
 * Delete voice by character ID
 */
export async function deleteVoiceByCharacterId(characterId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('character_voices')
      .delete()
      .eq('character_id', characterId);

    if (error) throw error;
  } catch (error) {
    console.error('Failed to delete character voice:', error);
    throw error;
  }
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Create a new CharacterVoice object with defaults
 */
export function createCharacterVoice(
  characterId: string,
  userId: string,
  overrides?: Partial<CharacterVoice>
): CharacterVoice {
  const { id, ...restOverrides } = overrides || {};
  return {
    id: id || generateId(),
    character_id: characterId,
    user_id: userId,
    voice_mode: 'voice_design',
    language: 'Auto',
    created_at: Date.now(),
    updated_at: Date.now(),
    ...restOverrides,
  };
}

/**
 * Get display name for a voice configuration
 */
export function getVoiceDisplayName(voice: CharacterVoice): string {
  switch (voice.voice_mode) {
    case 'custom_voice':
      return voice.preset_speaker || 'Custom Voice';
    case 'voice_clone':
      return 'Cloned Voice';
    case 'voice_design':
      return 'Designed Voice';
    default:
      return 'Voice';
  }
}

/**
 * Get mode label for UI
 */
export function getVoiceModeLabel(mode: CharacterVoice['voice_mode']): string {
  switch (mode) {
    case 'custom_voice':
      return 'Preset Speaker';
    case 'voice_clone':
      return 'Voice Clone';
    case 'voice_design':
      return 'Voice Design';
    default:
      return mode;
  }
}

/**
 * Preset speaker metadata for UI
 */
export const PRESET_SPEAKERS = [
  { id: 'Vivian', name: 'Vivian', description: 'Bright, slightly edgy young female', language: 'Chinese' },
  { id: 'Serena', name: 'Serena', description: 'Warm, gentle young female', language: 'Chinese' },
  { id: 'Uncle_Fu', name: 'Uncle Fu', description: 'Seasoned male, low mellow timbre', language: 'Chinese' },
  { id: 'Dylan', name: 'Dylan', description: 'Youthful Beijing male', language: 'Chinese (Beijing)' },
  { id: 'Eric', name: 'Eric', description: 'Lively Chengdu male', language: 'Chinese (Sichuan)' },
  { id: 'Ryan', name: 'Ryan', description: 'Dynamic male, strong rhythm', language: 'English' },
  { id: 'Aiden', name: 'Aiden', description: 'Sunny American male', language: 'English' },
  { id: 'Ono_Anna', name: 'Ono Anna', description: 'Playful Japanese female', language: 'Japanese' },
  { id: 'Sohee', name: 'Sohee', description: 'Warm Korean female', language: 'Korean' },
] as const;

/**
 * Supported languages for Qwen3-TTS
 */
export const SUPPORTED_LANGUAGES = [
  { id: 'Auto', name: 'Auto' },
  { id: 'Chinese', name: 'Chinese' },
  { id: 'English', name: 'English' },
  { id: 'Japanese', name: 'Japanese' },
  { id: 'Korean', name: 'Korean' },
  { id: 'German', name: 'German' },
  { id: 'French', name: 'French' },
  { id: 'Russian', name: 'Russian' },
  { id: 'Portuguese', name: 'Portuguese' },
  { id: 'Spanish', name: 'Spanish' },
  { id: 'Italian', name: 'Italian' },
] as const;

// ============================================
// SAVED VOICE SAMPLES (stored in character_voices.saved_samples JSONB)
// ============================================

/**
 * Upload saved voice sample audio to R2
 * Saved to: ezart-assets/character/final/{projectId}/{characterId}/voice/
 */
export async function uploadSavedVoiceAudio(
  audioBlob: Blob,
  userId: string,
  projectId: string,
  characterId: string
): Promise<string> {
  const filename = `saved_${Date.now()}.wav`;
  return uploadVoiceAudioToR2(audioBlob, userId, projectId, characterId, filename, false);
}

/**
 * Get all saved voice samples for a character (from JSONB column)
 */
export async function getSavedVoiceSamples(characterId: string): Promise<SavedVoiceSample[]> {
  try {
    const { data, error } = await supabase
      .from('character_voices')
      .select('saved_samples')
      .eq('character_id', characterId)
      .maybeSingle();

    if (error) throw error;
    const samples = (data?.saved_samples || []) as SavedVoiceSample[];
    // Sort by created_at descending
    return samples.sort((a, b) => b.created_at - a.created_at);
  } catch (error) {
    console.error('Failed to load saved voice samples:', error);
    return [];
  }
}

/**
 * Save a voice sample (add or update in JSONB array)
 */
export async function saveSavedVoiceSample(sample: SavedVoiceSample): Promise<SavedVoiceSample> {
  try {
    // Get existing samples
    const existing = await getSavedVoiceSamples(sample.character_id);

    // If setting as default, clear others
    let updated = existing.map(s =>
      sample.is_default ? { ...s, is_default: false } : s
    );

    // Add or update sample
    const idx = updated.findIndex(s => s.id === sample.id);
    if (idx >= 0) {
      updated[idx] = sample;
    } else {
      updated.unshift(sample); // Add to front
    }

    // Save back to database
    const { error } = await supabase
      .from('character_voices')
      .update({ saved_samples: updated })
      .eq('character_id', sample.character_id);

    if (error) throw error;
    return sample;
  } catch (error) {
    console.error('Failed to save voice sample:', error);
    throw error;
  }
}

/**
 * Set a voice sample as the default
 */
export async function setDefaultVoiceSample(sampleId: string, characterId: string): Promise<void> {
  try {
    const samples = await getSavedVoiceSamples(characterId);
    const updated = samples.map(s => ({
      ...s,
      is_default: s.id === sampleId
    }));

    const { error } = await supabase
      .from('character_voices')
      .update({ saved_samples: updated })
      .eq('character_id', characterId);

    if (error) throw error;
  } catch (error) {
    console.error('Failed to set default voice sample:', error);
    throw error;
  }
}

/**
 * Delete a saved voice sample
 */
export async function deleteSavedVoiceSample(sampleId: string, characterId: string): Promise<void> {
  try {
    const samples = await getSavedVoiceSamples(characterId);
    const updated = samples.filter(s => s.id !== sampleId);

    const { error } = await supabase
      .from('character_voices')
      .update({ saved_samples: updated })
      .eq('character_id', characterId);

    if (error) throw error;
  } catch (error) {
    console.error('Failed to delete voice sample:', error);
    throw error;
  }
}

/**
 * Create a new SavedVoiceSample object
 */
export function createSavedVoiceSample(
  characterId: string,
  userId: string,
  audioUrl: string,
  quote: string,
  voiceConfig: SavedVoiceSample['voice_config'],
  overrides?: Partial<SavedVoiceSample>
): SavedVoiceSample {
  return {
    id: generateId(),
    character_id: characterId,
    user_id: userId,
    audio_url: audioUrl,
    quote,
    voice_config: voiceConfig,
    is_default: false,
    created_at: Date.now(),
    ...overrides,
  };
}
